/* -----------------Globals--------------- */
import type React from "react";
import { useState } from "react";

import { defaultAiAssistantInfo } from "@memorang/helpers/src/aiAssistant";
/* -----------------Helpers & Hooks--------------- */
import {
	SearchContextProvider,
	useSearchContext,
} from "./contexts/SearchContext";
import { useSearchView } from "./hooks/useSearchView";

import type { AiAssistantInfo } from "@memorang/ui/src/components/ChatUI/types";
/* -----------------Types--------------- */
import type { FeedbackParams } from "../feedback/types";
import type { HandleTracking } from "../types";
import type { EpubParams, VectorStoreParams } from "./types";

/* -----------------Child components--------------- */
import Box from "@memorang/ui/src/components/Box";
import { ChatUI } from "@memorang/ui/src/components/ChatUI/ChatUI";
import { useTranslation } from "react-i18next";
import TTSContainer from "../TTS/TTSContainer";

type Props = {
	context: string;
	handleTracking: HandleTracking;
	vectorStoreParams: VectorStoreParams;
	userId: string;
	handleClose?: () => void;
	defaultQuery?: string;
	feedbackDialogParams?: FeedbackParams;
	epubParams?: EpubParams;
	handleNavigateToReference?: (url: string) => void;
	bookIds?: string[];
	filterSourceIds?: number[];
	searchContextMenu?: React.ReactNode;
	searchContext?: string;
	referenceThumbnails?: Record<string, string>;
	assistantInfo?: AiAssistantInfo;
};

type InnerAiLAStaticProps = Props & {
	constructEpubParams: EpubParams | undefined;
	setConstructEpubParams: React.Dispatch<
		React.SetStateAction<EpubParams | undefined>
	>;
};

// TODO: Change component name
export const AiLAStatic = (props: Props) => {
	const [constructEpubParams, setConstructEpubParams] = useState(
		props.epubParams,
	);
	const { i18n } = useTranslation();
	return (
		<SearchContextProvider
			defaultQuery={props.defaultQuery}
			userId={props.userId}
			outputLanguage={i18n.language}
			vectorStoreParams={props.vectorStoreParams}
			epubParams={constructEpubParams}
			context={props.context}
			sessionId={props.feedbackDialogParams?.sessionId!}
			assistantInfo={props.assistantInfo ?? defaultAiAssistantInfo}
			activityType="reader"
		>
			<InnerAiLAStatic
				{...props}
				constructEpubParams={constructEpubParams}
				setConstructEpubParams={setConstructEpubParams}
			/>
		</SearchContextProvider>
	);
};

const InnerAiLAStatic = ({
	feedbackDialogParams,
	handleTracking,
	epubParams,
	handleNavigateToReference,
	filterSourceIds,
	searchContext,
	setConstructEpubParams,
	referenceThumbnails,
	vectorStoreParams,
}: InnerAiLAStaticProps) => {
	const emptyStateOptions = epubParams
		? [
				{
					label: "Summarize this page",
					action: () => interceptSearchQuery("Summarize this page", true),
				},
				// TODO: Uncomment when we have the right implementation
				// {
				//   label: `Ask anything about this page`,
				//   action: () => {
				//     setShouldFocusInput(true);
				//   },
				// },
				// {
				//   label: 'Recommend some questions to ask',
				//   action: () => interceptSearchQuery(`Recommend some questions to ask`),
				// },
				// {
				//   label: `Ask anything across all books`,
				//   action: () =>
				//     interceptSearchQuery(`Ask anything across all books`, false, 'global'),
				// },
				// {
				//   label: `Ask anything about ${context}`,
				//   action: () =>
				//     interceptSearchQuery(`Ask anything about ${context}`, false, 'aila'),
				// },
			]
		: [];

	const {
		loading,
		streaming,
		context,
		showReference,
		searchQuery,
		messages,
		abortSearch,
		assistantInfo,
		isThreadLoading,
	} = useSearchContext();

	const {
		handleSelectQuery,
		localRecommendedQueries,
		messageActions,
		messageFeedbackComponents,
		isRecommendedQuestionsFetching,
		interceptSearchQuery,
		handleResetChatState,
	} = useSearchView({
		epubParams,
		feedbackDialogParams,
		handleTracking,
		setConstructEpubParams,
		filterSourceIds,
		searchContext,
		handleSelectSummarizeChip: () =>
			interceptSearchQuery("Summarize this page", true),
	});

	return (
		<Box position="relative" height="100%">
			<ChatUI>
				<ChatUI.ScrollView>
					<ChatUI.MessageList
						assistantInfo={assistantInfo}
						messages={messages}
						isRecommendedQuestionsFetching={isRecommendedQuestionsFetching}
						loading={loading}
						streaming={streaming}
						recommendedQueries={localRecommendedQueries}
						handleSelectQuery={handleSelectQuery}
						context={context}
						showSummarizeResponse={!(loading || streaming)}
						handleNavigateToReference={handleNavigateToReference}
						emptyStateProps={{
							emptyStateOptions,
						}}
						showReference={showReference}
						messageActions={messageActions}
						referenceThumbnails={referenceThumbnails}
						isThreadLoading={isThreadLoading}
						textToSpeach={(_runId, content) => (
							<TTSContainer
								itemId={vectorStoreParams.itemId ?? ""}
								explanation={content}
								size={18}
								disabled={streaming}
							/>
						)}
					/>
				</ChatUI.ScrollView>
				<ChatUI.BottomAppBar
					showConversationMode
					showInlineButtons
					searchQuery={searchQuery.value}
					interceptSearchQuery={interceptSearchQuery}
					searchPlaceholder={context}
					searchContextMenu={`${context} can make mistakes. Check important info.`}
					handleReset={handleResetChatState}
					disableSearch={loading || streaming}
					handleAbortSearch={abortSearch}
				/>
			</ChatUI>
			{messageFeedbackComponents}
		</Box>
	);
};
